import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import {
  CustomiseItemsTransferForm,
  EMPTY_CIT_PENDING_FILES,
  type CitPendingFiles,
} from '../../components/forms/CustomiseItemsTransferForm';
import {
  allocateCitStockTransferNos,
  CIT_OPEN_ACCESS,
  submitCit,
  updateCit,
  uploadCitAttachment,
  validateCitForm,
} from '../../lib/citSubmissions';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useFormWorkflowPermissions } from '../../hooks/useFormWorkflowPermissions';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import {
  createEmptyCitForm,
  type CustomiseItemsTransferFormData,
} from '../../types/applicationForm';

export function CustomiseItemsTransferNewPage() {
  const { basePath } = usePortal();
  const navigate = useNavigate();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const { canSubmitInternal, loading: permissionsLoading } =
    useFormWorkflowPermissions('customise-items-transfer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<CustomiseItemsTransferFormData>(() => createEmptyCitForm());
  const [pendingFiles, setPendingFiles] = useState<CitPendingFiles>({
    ...EMPTY_CIT_PENDING_FILES,
    deposit: {},
    payment: {},
    offset: {},
    clearing: {},
  });

  const listPath = basePath
    ? `${basePath}/customise-items-transfer`
    : '/customise-items-transfer';

  const submitterLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  async function handleSave() {
    const validationError = validateCitForm(form, pendingFiles);
    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const created = await submitCit(
        form,
        submitterLabel || undefined,
        authUser?.email ?? undefined,
      );

      // Upload first, then allocate ST nos — avoids burning sequence numbers if upload fails.
      let nextForm: CustomiseItemsTransferFormData = form;

      if (pendingFiles.sales_confirmation) {
        nextForm = {
          ...nextForm,
          sales_confirmation_file: await uploadCitAttachment(
            created.id,
            'sales_confirmation',
            pendingFiles.sales_confirmation,
          ),
        };
      }

      nextForm = {
        ...nextForm,
        deposit_invoices: await Promise.all(
          nextForm.deposit_invoices.map(async (doc) => {
            let next = doc;
            const depositFile = pendingFiles.deposit[doc.id];
            if (depositFile) {
              next = {
                ...next,
                file: await uploadCitAttachment(created.id, 'deposit_invoice', depositFile, doc.id),
              };
            }
            next = {
              ...next,
              payment_receipts: await Promise.all(
                next.payment_receipts.map(async (receipt) => {
                  const paymentFile = pendingFiles.payment[receipt.id];
                  if (!paymentFile) return receipt;
                  return {
                    ...receipt,
                    file: await uploadCitAttachment(
                      created.id,
                      'payment_receipt',
                      paymentFile,
                      receipt.id,
                    ),
                  };
                }),
              ),
            };
            return next;
          }),
        ),
        offset_invoices: await Promise.all(
          nextForm.offset_invoices.map(async (doc) => {
            const file = pendingFiles.offset[doc.id];
            if (!file) return doc;
            return {
              ...doc,
              file: await uploadCitAttachment(created.id, 'offset_invoice', file, doc.id),
            };
          }),
        ),
        clearing_invoices: await Promise.all(
          (nextForm.clearing_invoices ?? []).map(async (doc) => {
            const file = pendingFiles.clearing[doc.id];
            if (!file) return doc;
            return {
              ...doc,
              file: await uploadCitAttachment(created.id, 'clearing_invoice', file, doc.id),
            };
          }),
        ),
      };

      nextForm = await allocateCitStockTransferNos(nextForm);
      await updateCit(created.id, nextForm);
      navigate(`${listPath}/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry.');
    } finally {
      setBusy(false);
    }
  }

  if (permissionsLoading && !CIT_OPEN_ACCESS) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Checking submission permissions…</p>
      </div>
    );
  }

  if (!CIT_OPEN_ACCESS && !canSubmitInternal) {
    return (
      <div className="af-applications-page af-form-page">
        <PageHeader title="New Sales Confirmation" />
        <div className="alert alert-error">
          You do not have permission to submit Sales Confirmation forms.
        </div>
        <Link to={listPath} className="btn-secondary btn-sm">
          Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page">
      <PageHeader
        title="Manual Sales Confirmation"
        description="Prefer Sync from PowerBI on the list for 2026 SOC quotes. Use this only for exceptions."
        actions={
          <Link to={listPath} className="btn-secondary btn-sm">
            Back to list
          </Link>
        }
      />

      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="card af-form-card cit-form-card">
        <CustomiseItemsTransferForm
          value={form}
          onChange={setForm}
          pendingFiles={pendingFiles}
          onPendingFilesChange={setPendingFiles}
          disabled={busy}
        />
        <div className="crm-form__actions cit-form__actions">
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => navigate(listPath)}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void handleSave()}>
            {busy ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
}
